import axios from 'axios';

const axiosServices = axios.create({
  baseURL: `${process.env.REACT_APP_BASE_URL}`,
});

// Guards against the "page keeps reloading on every screen" loop:
// a stale token 401s -> refresh fails -> we redirect to "/", but redux-persist
// still has `tokens` in localStorage, so withAuthRedirect bounces back to
// /dashboard, which 401s again... forever. Clearing the persisted auth state
// before redirecting breaks that cycle.
let isLoggingOut = false;
let refreshPromise = null;

const clearPersistedAuth = () => {
  try {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');

    const raw = localStorage.getItem('persist:root');
    if (raw) {
      const parsed = JSON.parse(raw);
      // redux-persist stores each slice as a JSON string inside the root object
      parsed.tokens = JSON.stringify({ token: null, refreshToken: null, user: null });
      localStorage.setItem('persist:root', JSON.stringify(parsed));
    }
  } catch (e) {
    // ignore storage/parse errors – we still redirect below
  }
};

const forceLogout = () => {
  if (isLoggingOut) return;
  isLoggingOut = true;
  clearPersistedAuth();

  // Only navigate if we're not already on the login route, otherwise we'd
  // just keep reloading the login page.
  if (window.location.pathname !== '/') {
    window.location.replace('/');
  }
};

const requestNewAccessToken = async () => {
  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  const { data } = await axios.post(
    `${process.env.REACT_APP_BASE_URL}/refresh-token`,
    { refreshToken }
  );

  const newAccessToken = data.accessToken || data.token;
  if (!newAccessToken) {
    throw new Error('Refresh response did not include an access token');
  }

  localStorage.setItem('accessToken', newAccessToken);
  if (data.refreshToken) {
    localStorage.setItem('refreshToken', data.refreshToken);
  }
  return newAccessToken;
};

// Request Interceptor: Attach access token
axiosServices.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response Interceptor: Refresh token on 401
axiosServices.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;

      try {
        // De-dupe concurrent refreshes so a burst of 401s triggers a single
        // /refresh-token call (and a single redirect on failure).
        if (!refreshPromise) {
          refreshPromise = requestNewAccessToken().finally(() => {
            refreshPromise = null;
          });
        }
        const newAccessToken = await refreshPromise;

        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return axiosServices(originalRequest);
      } catch (err) {
        forceLogout();
        return Promise.reject(err);
      }
    }

    return Promise.reject(error);
  }
);

export default axiosServices;
